/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { LexicalEditor } from 'lexical';
import * as React from 'react';
import { useMemo, useState } from 'react';

import { INSERT_LAYOUT_COMMAND, INSERT_TAB_COMMAND } from './LayoutPlugin';
import {
  Button,
  ButtonColor,
} from '../../../../../../components/uikit/buttons/Button.tsx';
import { useScopedTranslation } from '../../../../../../application/localization/useScopedTranslation.ts';
import { RadioButton } from '../../../../../../components/uikit/RadioButton.tsx';
import commonStyles from '../commonStyles.module.scss';
import clsx from 'clsx';
import styles from '../commonStyles.module.scss';

export default function InsertLayoutDialog({
  activeEditor,
  onClose,
  isTab,
}: {
  activeEditor: LexicalEditor;
  onClose: () => void;
  isTab?: boolean;
}): React.JSX.Element {
  const i18n = useScopedTranslation('Page.PageDesigner');

  const LAYOUTS = useMemo(() => {
    return [
      {
        label: `2 ${i18n.t('columns').toLowerCase()} (${i18n.t(
          'equal_width',
        )})`,
        value: '1fr 1fr',
      },
      {
        label: `2 ${i18n.t('columns').toLowerCase()} (25% - 75%)`,
        value: '1fr 3fr',
      },
      {
        label: `3 ${i18n.t('columns').toLowerCase()} (${i18n.t(
          'equal_width',
        )})`,
        value: '1fr 1fr 1fr',
      },
      {
        label: `3 ${i18n.t('columns').toLowerCase()} (25% - 50% - 25%)`,
        value: '1fr 2fr 1fr',
      },
      {
        label: `4 ${i18n.t('columns').toLowerCase()} (${i18n.t(
          'equal_width',
        )})`,
        value: '1fr 1fr 1fr 1fr',
      },
    ];
  }, []);

  const LAYOUTSTab = useMemo(() => {
    return [
      { label: `2 ${i18n.t('tabs').toLowerCase()}`, value: '1fr 1fr' },
      { label: `3 ${i18n.t('tabs').toLowerCase()}`, value: '1fr 1fr 1fr' },
      { label: `4 ${i18n.t('tabs').toLowerCase()}`, value: '1fr 1fr 1fr 1fr' },
    ];
  }, []);

  const layouts = isTab ? LAYOUTSTab : LAYOUTS;

  const [layout, setLayout] = useState(layouts[0].value);

  const onClick = () => {
    activeEditor.dispatchCommand(
      isTab ? INSERT_TAB_COMMAND : INSERT_LAYOUT_COMMAND,
      layout,
    );
    onClose();
  };

  return (
    <>
      <div
        className={clsx(
          commonStyles.columnContainer,
          commonStyles.rowGap10,
          commonStyles.marginBottom70,
        )}
      >
        {layouts.map(({ label, value }) => (
          <RadioButton
            title={label}
            value={value}
            onClick={() => setLayout(value)}
            defaultChecked={layout === value}
            name={'layout'}
            key={value}
          />
        ))}
      </div>
      <div className={styles.buttonContainer}>
        <Button
          onClick={onClose}
          title={i18n.t('cancel')}
          color={ButtonColor.Secondary}
        />
        <Button
          onClick={onClick}
          title={isTab ? i18n.t('insert_tabs') : i18n.t('insert_columns')}
          color={ButtonColor.Primary}
        />
      </div>
    </>
  );
}
